import React, { useMemo, useRef, useLayoutEffect } from "react";
import { connect } from "react-redux";
import { Form } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";

import { EMPTY_STRING, ELIGIBILITY_OPTIONS } from "../../../../constants";
import SurveyFormField from "../../../../shared/components/surveyFormField/SurveyFormField";

const ConfirmEligibleCountries = ({
  allowedCountries,
  allCountries,
  isModal = false,
  userChoice,
}) => {
  const eligibleCountryRef = useRef(null);
  const nonEligibleCountryRef = useRef(null);
  const eligibleCountryScrollPosition = useRef(0);
  const nonEligibleCountryScrollPosition = useRef(0);

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const isEligibleToBuy = watch("isEligibleToBuy");

  const selectedCountryIds = useWatch({ name: "countriesLikeToLiveIds" }) || [];

  // Restore scroll position after DOM updates
  useLayoutEffect(() => {
    if (eligibleCountryRef.current) {
      eligibleCountryRef.current.scrollTop =
        eligibleCountryScrollPosition.current;
    }

    if (nonEligibleCountryRef.current) {
      nonEligibleCountryRef.current.scrollTop =
        nonEligibleCountryScrollPosition.current;
    }
  });

  const filteredEligibleCountries = useMemo(() => {
    if (eligibleCountryRef.current) {
      eligibleCountryScrollPosition.current =
        eligibleCountryRef.current.scrollTop;
    }

    const list = allCountries?.filter((country) =>
      allowedCountries.includes(country.id)
    );

    const selected = list.filter((c) => selectedCountryIds.includes(c.id));
    const others = list.filter((c) => !selectedCountryIds.includes(c.id));

    return [...selected, ...others];
  }, [allCountries, allowedCountries, selectedCountryIds]);

  const filteredNonEligibleCountries = useMemo(() => {
    if (nonEligibleCountryRef.current) {
      nonEligibleCountryScrollPosition.current =
        nonEligibleCountryRef.current.scrollTop;
    }

    const list = allCountries?.filter(
      (country) =>
        userChoice?.includes(country.id) &&
        !allowedCountries.includes(country.id)
    );

    const selected = list.filter((c) => selectedCountryIds.includes(c.id));
    const others = list.filter((c) => !selectedCountryIds.includes(c.id));

    return [...selected, ...others];
  }, [allCountries, allowedCountries, userChoice, selectedCountryIds]);

  return (
    <div
      className={`${!isModal ? "eligible-countries-container" : EMPTY_STRING}`}
    >
      <div className="question-heading">
        You are eligible to buy a house in the following countries۔ Kindly
        select which countries you want to live in.
      </div>
      {errors.countriesLikeToLiveIds && (
        <div className="error">{errors.countriesLikeToLiveIds.message}</div>
      )}
      <div className="form-fields modal-form-fields" ref={eligibleCountryRef}>
        {filteredEligibleCountries.map((country) => (
          <SurveyFormField
            name="countriesLikeToLiveIds"
            key={country.id}
            variant="radio"
            label={country.name}
            value={country.id}
            multiple
            endAdornment={
              <div className="position-absolute flags-adornment">
                <img
                  src={country.flag}
                  className="country-flag"
                  alt={EMPTY_STRING}
                />
              </div>
            }
          />
        ))}
      </div>
      <div className="question-heading">
        Would you like to see the properties you selected in countries where you
        are not eligible to buy?
      </div>
      <div className="d-flex flex-column flex-md-row gap-1 gap-md-5 eligibility-radio-group">
        <Form.Check
          className="mb-0"
          {...register("isEligibleToBuy")}
          name="isEligibleToBuy"
          type="radio"
          label="Yes"
          value={ELIGIBILITY_OPTIONS.YES}
        />
        <Form.Check
          className="mb-0"
          {...register("isEligibleToBuy")}
          name="isEligibleToBuy"
          type="radio"
          label="No"
          value={ELIGIBILITY_OPTIONS.NO}
        />
      </div>
      {isEligibleToBuy == ELIGIBILITY_OPTIONS.YES && (
        <div
          className="form-fields modal-form-fields"
          ref={nonEligibleCountryRef}
        >
          {filteredNonEligibleCountries.map((country) => (
            <SurveyFormField
              name="countriesLikeToLiveIds"
              key={country.id}
              variant="radio"
              label={country.name}
              value={country.id}
              multiple
              endAdornment={
                <div className="position-absolute flags-adornment">
                  <img
                    src={country.flag}
                    className="country-flag"
                    alt={EMPTY_STRING}
                  />
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  allCountries: state.countryReducer.allCountries,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfirmEligibleCountries);
